import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * useScrollRestore — persists the window scrollY for a given feed key in
 * sessionStorage so users return to the same scroll position after viewing a
 * listing detail and pressing Back. Cooperates with React Router's POP nav
 * type so it doesn't restore on fresh navigations (PUSH/REPLACE).
 *
 * Usage in a feed page:
 *   useScrollRestore('homepage', { ready: !loading && listings.length > 0 });
 *
 * Usage in the card's <Link> click:
 *   onClick={() => saveFeedScroll('homepage')}
 *
 * `ready` defers the scroll until the feed actually rendered the items —
 * prevents scrolling to 1800px before any cards exist.
 */
const STORAGE_PREFIX = 'genzox_scroll_';

export const saveFeedScroll = (key) => {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(window.scrollY || 0));
  } catch (_) { /* ignore quota / private-mode errors */ }
};

export const clearFeedScroll = (key) => {
  try { sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`); } catch (_) {}
};

const useScrollRestore = (key, { ready = true } = {}) => {
  const location = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const restored = useRef(false);

  // Save current scroll on every Back/refresh trigger
  useEffect(() => {
    const handler = () => saveFeedScroll(key);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      // Save on unmount too (when user clicks a listing card and unmounts the feed)
      saveFeedScroll(key);
    };
  }, [key]);

  // Restore after content is ready and this is a POP (Back) navigation
  useEffect(() => {
    if (restored.current) return;
    if (!ready) return;
    if (navType !== 'POP') return;
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    const y = raw ? parseInt(raw, 10) : 0;
    if (Number.isFinite(y) && y > 0) {
      // Defer to next paint so the listings are actually laid out
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
      });
    }
    restored.current = true;
  }, [key, ready, navType, location.key]);
};

export default useScrollRestore;
